import { CUSTOM_FIELD_TABS } from '@/vacancy_request/components/template-request-form/custom-fields/constants';

export const CUSTOM_FIELD_REQUIRED_LEVELS = {
  DISABLED: 0,
  REQUIRED: 1,
  OPTIONAL: 2
};

const KNOWN_TABS = Object.values(CUSTOM_FIELD_TABS);
const LAST_TAB = CUSTOM_FIELD_TABS.CANDIDATE_DETAILS;

const normalizeRequiredLevel = (value) => {
  if (typeof value === 'number' && [0, 1, 2].includes(value)) return value;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '0') return CUSTOM_FIELD_REQUIRED_LEVELS.DISABLED;
    if (normalized === '1') return CUSTOM_FIELD_REQUIRED_LEVELS.REQUIRED;
    if (normalized === '2') return CUSTOM_FIELD_REQUIRED_LEVELS.OPTIONAL;
    if (normalized === 'true') return CUSTOM_FIELD_REQUIRED_LEVELS.REQUIRED;
    if (normalized === 'false') return CUSTOM_FIELD_REQUIRED_LEVELS.OPTIONAL;
  }

  if (value === true) return CUSTOM_FIELD_REQUIRED_LEVELS.REQUIRED;
  if (value === false) return CUSTOM_FIELD_REQUIRED_LEVELS.OPTIONAL;

  return CUSTOM_FIELD_REQUIRED_LEVELS.OPTIONAL;
};

/**
 * ÚNICO punto del formulario de solicitud que conoce la forma en que el api devuelve los campos
 * custom de una plantilla. Todo lo demás (render, validación, lectura) trabaja contra el view
 * model que sale de acá.
 *
 * Mientras el modelo de datos no esté cerrado (ver open-questions.md §1-§2), este archivo es lo
 * único que hay que cambiar cuando se defina. Si lo que llega no tiene la forma esperada, devuelve
 * una lista vacía: el formulario sigue funcionando sin campos custom.
 *
 * Los deshabilitados (`required: 0`) se descartan acá: el ticket dice que no se muestran en el
 * formulario de solicitud.
 */
export const mapTemplateCustomFields = (templateConfig) => {
  const rawFields = Array.isArray(templateConfig?.customFields) ? templateConfig.customFields : [];

  return rawFields
    .map((field, index) => {
      const fieldId = field?._id ? String(field._id) : '';
      const options = Array.isArray(field?.options) ? field.options.filter(Boolean).map(String) : [];

      return {
        // Nombre del campo en react-hook-form. El `_id` es la identidad estable del campo;
        // el fallback por índice sólo aplica a datos de prueba sin `_id`.
        key: fieldId || `index-${index}`,
        fieldId,
        tab: field?.tab,
        name: field?.name ? String(field.name) : '',
        label: field?.label ? String(field.label) : '',
        type: field?.type,
        options,
        requiredLevel: normalizeRequiredLevel(field?.required),
        order: Number.isFinite(field?.order) ? field.order : index
      };
    })
    .filter((field) => field.label && field.requiredLevel !== CUSTOM_FIELD_REQUIRED_LEVELS.DISABLED)
    .sort((a, b) => a.order - b.order);
};

/**
 * Campos de una pestaña. La última recoge además los de pestaña desconocida, que es la regla
 * documentada para cuando una plantilla vieja quede con un `tab` que ya no existe: se muestran
 * al final en vez de desaparecer.
 */
export const getCustomFieldsForTab = (fields, tab) =>
  fields.filter((field) => {
    if (field.tab === tab) return true;
    return tab === LAST_TAB && !KNOWN_TABS.includes(field.tab);
  });

export const isCustomFieldRequired = (field) => field.requiredLevel === CUSTOM_FIELD_REQUIRED_LEVELS.REQUIRED;

export const buildCustomFieldAnswerName = (field) => `customFieldsAnswers.${field.key}`;

/**
 * Lectura de una solicitud ya enviada: la lista de campos sale del **snapshot** guardado en la
 * solicitud, no de la plantilla vigente (D4). Es lo que hace que editar la plantilla no cambie
 * retroactivamente lo que muestra una solicitud vieja.
 *
 * En lectura un campo cerrado muestra **todas** sus opciones con la respondida marcada, y las toma
 * del snapshot: son las que existían al responder, no las que tenga la plantilla hoy. Las respuestas
 * guardadas antes de que el snapshot incluyera `options` caen al único valor conocido, la respuesta.
 */
/**
 * Opciones a mostrar en lectura. Las respuestas guardadas antes de que el snapshot incluyera
 * `options` caen al único valor conocido: la propia respuesta.
 */
const snapshotOptions = (answer, value) => {
  if (Array.isArray(answer.options) && answer.options.length > 0) return answer.options.map(String);
  return value ? [value] : [];
};

export const mapRequestCustomFields = (dataForm) => {
  const rawAnswers = Array.isArray(dataForm?.customFieldsAnswers) ? dataForm.customFieldsAnswers : [];

  return rawAnswers
    .filter((answer) => answer && answer.label)
    .map((answer, index) => {
      const fieldId = answer.fieldId ? String(answer.fieldId) : '';
      const value = answer.value === undefined || answer.value === null ? '' : String(answer.value);

      return {
        key: fieldId || `answer-${index}`,
        fieldId,
        tab: answer.tab,
        name: answer.name ? String(answer.name) : '',
        label: String(answer.label),
        type: answer.type,
        options: snapshotOptions(answer, value),
        // En lectura el nivel no cumple ninguna función: no hay validación y el control está
        // deshabilitado. Marcarlo obligatorio sólo pondría un asterisco engañoso.
        requiredLevel: CUSTOM_FIELD_REQUIRED_LEVELS.OPTIONAL,
        order: index
      };
    });
};

/**
 * Escritura: forma interna del formulario → payload del api.
 *
 * Se arma recorriendo **los campos vigentes de la plantilla**, no las respuestas cargadas: así lo
 * que se envía es exactamente lo que la plantilla pide hoy. Las respuestas a campos que ya no
 * existen se descartan acá, que es la decisión D11.
 *
 * Se manda como objeto indexado por `fieldId`. El api acepta también un array de `{fieldId, value}`.
 */
export const buildCustomFieldAnswersPayload = (fields, answersByFieldId) => {
  if (!Array.isArray(fields) || fields.length === 0) return null;
  if (!answersByFieldId || typeof answersByFieldId !== 'object') return null;

  const payload = {};

  fields.forEach((field) => {
    if (!field?.fieldId) return;

    const value = answersByFieldId[field.key];
    if (value === undefined || value === null || value === '') return;

    payload[field.fieldId] = value;
  });

  return Object.keys(payload).length > 0 ? payload : null;
};

/**
 * Dirección de lectura del mismo límite: respuestas guardadas en la solicitud → forma interna
 * del formulario (`{ [fieldId]: valor }`), que es contra la que registran los campos.
 *
 * Hace falta porque el cargador del formulario vuelca el documento de la solicitud clave por
 * clave con `setValue`, y la forma en que el api guarda las respuestas no tiene por qué coincidir
 * con la forma interna. Cuando se cierre el modelo, esto se ajusta acá y en ningún otro lado.
 */
export const mapRequestCustomFieldAnswers = (dataForm) => {
  const rawAnswers = Array.isArray(dataForm?.customFieldsAnswers) ? dataForm.customFieldsAnswers : [];

  return rawAnswers.reduce((answersByField, answer) => {
    const key = answer?.fieldId ? String(answer.fieldId) : '';
    if (!key) return answersByField;

    return { ...answersByField, [key]: answer?.value ?? '' };
  }, {});
};
